/**
 * Application questions (PRD §7.4): the four default questions as configurable
 * rows (enable / required / compatible answer type) plus fully custom typed
 * questions with add, edit, reorder, duplicate, and delete.
 */
import { useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text, TextInput } from '@/components/common/AppText';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Plus,
  Trash2,
} from 'lucide-react-native';
import { borderRadius, colors, spacing } from '@tabbio/tokens';
import type {
  JobScreeningQuestionType,
  JobScreeningQuestionV2,
} from '@tabbio/types';
import { useTheme } from '../../../hooks/useTheme';
import { useHaptics } from '../../../hooks/useHaptics';
import {
  DEFAULT_QUESTION_DEFS,
  QUESTION_TYPE_VALUES,
  createQuestionId,
  moveListItem,
} from './post-job-machine';
import { OrderedListEditor } from './OrderedListEditor';
import {
  FieldLabel,
  FlowDropdown,
  InlineError,
  fieldStyles,
  type FlowOption,
} from './post-job-ui';
import { AppSwitch } from '../../common/AppSwitch';
import type { PostJobFlowController } from './use-post-job-flow';

function useQuestionTypeOptions(
  types: readonly JobScreeningQuestionType[],
): FlowOption<JobScreeningQuestionType>[] {
  const { t } = useTranslation('employer');
  return useMemo(
    () =>
      types.map((value) => ({
        value,
        label: t(`jobFlow.questions.types.${value}` as never) as string,
      })),
    [t, types],
  );
}

function DefaultQuestionRow({
  defIndex,
  flow,
}: {
  defIndex: number;
  flow: PostJobFlowController;
}) {
  const { t } = useTranslation('employer');
  const { theme } = useTheme();

  const def = DEFAULT_QUESTION_DEFS[defIndex];
  const config = flow.state.questions.defaults.find(
    (item) => item.key === def.key,
  );
  const typeOptions = useQuestionTypeOptions(def.compatibleTypes);

  if (!config) return null;

  const patchConfig = (patch: Partial<typeof config>) => {
    flow.setQuestions({
      defaults: flow.state.questions.defaults.map((item) =>
        item.key === def.key ? { ...item, ...patch } : item,
      ),
    });
  };

  return (
    <View
      style={[
        styles.defaultRow,
        defIndex < DEFAULT_QUESTION_DEFS.length - 1 && {
          borderBottomColor: theme.borderSubtle,
          borderBottomWidth: StyleSheet.hairlineWidth,
        },
      ]}
    >
      <View style={styles.defaultHeader}>
        <Text style={[styles.defaultPrompt, { color: theme.text }]}>
          {t(def.promptKey as never) as string}
        </Text>
        <AppSwitch
          value={config.enabled}
          onValueChange={(enabled) => patchConfig({ enabled })}
          accessibilityLabel={t(def.promptKey as never) as string}
        />
      </View>

      {config.enabled ? (
        <View style={styles.defaultControls}>
          <View style={fieldStyles.flexOne}>
            <FlowDropdown
              label={t('jobFlow.questions.answerType') as string}
              value={config.type}
              placeholder={t('jobFlow.questions.answerType') as string}
              options={typeOptions}
              onChange={(type) => patchConfig({ type })}
            />
          </View>
          <View style={styles.requiredToggle}>
            <Text style={[styles.requiredLabel, { color: theme.textSecondary }]}>
              {t('jobFlow.questions.required')}
            </Text>
            <AppSwitch
              value={config.required}
              onValueChange={(required) => patchConfig({ required })}
              accessibilityLabel={t('jobFlow.questions.required') as string}
            />
          </View>
        </View>
      ) : null}
    </View>
  );
}

function CustomQuestionCard({
  question,
  index,
  total,
  flow,
}: {
  question: JobScreeningQuestionV2;
  index: number;
  total: number;
  flow: PostJobFlowController;
}) {
  const { t } = useTranslation('employer');
  const { theme } = useTheme();
  const haptics = useHaptics();
  const typeOptions = useQuestionTypeOptions(QUESTION_TYPE_VALUES);

  const custom = flow.state.questions.custom;

  const patchQuestion = (patch: Partial<JobScreeningQuestionV2>) => {
    flow.setQuestions({
      custom: custom.map((item) =>
        item.id === question.id ? { ...item, ...patch } : item,
      ),
    });
  };

  const move = (direction: -1 | 1) => {
    haptics.trigger('selection');
    flow.setQuestions({
      custom: moveListItem(custom, index, index + direction),
    });
  };

  const duplicate = () => {
    haptics.trigger('light');
    const copy: JobScreeningQuestionV2 = {
      ...question,
      id: createQuestionId(),
      options: question.options ? [...question.options] : undefined,
    };
    const next = [...custom];
    next.splice(index + 1, 0, copy);
    flow.setQuestions({ custom: next });
  };

  const remove = () => {
    haptics.trigger('light');
    flow.setQuestions({
      custom: custom.filter((item) => item.id !== question.id),
    });
  };

  return (
    <View
      style={[
        styles.customCard,
        { backgroundColor: theme.surface, borderColor: theme.borderSubtle },
      ]}
    >
      <View style={styles.customHeader}>
        <Text style={[styles.customIndex, { color: theme.textTertiary }]}>
          {t('jobFlow.questions.customLabel', { number: index + 1 })}
        </Text>
        <View style={styles.customActions}>
          <Pressable
            onPress={() => move(-1)}
            disabled={index === 0}
            hitSlop={6}
            accessibilityLabel={t('jobFlow.list.moveUp') as string}
            style={[styles.iconButton, index === 0 && styles.iconDisabled]}
          >
            <ChevronUp size={15} color={theme.textTertiary} strokeWidth={2.2} />
          </Pressable>
          <Pressable
            onPress={() => move(1)}
            disabled={index === total - 1}
            hitSlop={6}
            accessibilityLabel={t('jobFlow.list.moveDown') as string}
            style={[styles.iconButton, index === total - 1 && styles.iconDisabled]}
          >
            <ChevronDown size={15} color={theme.textTertiary} strokeWidth={2.2} />
          </Pressable>
          <Pressable
            onPress={duplicate}
            hitSlop={6}
            accessibilityLabel={t('jobFlow.questions.duplicate') as string}
            style={styles.iconButton}
          >
            <Copy size={14} color={theme.textTertiary} strokeWidth={2.2} />
          </Pressable>
          <Pressable
            onPress={remove}
            hitSlop={6}
            accessibilityLabel={t('jobFlow.questions.delete') as string}
            style={styles.iconButton}
          >
            <Trash2 size={14} color={colors.danger} strokeWidth={2.2} />
          </Pressable>
        </View>
      </View>

      <TextInput
        value={question.prompt}
        onChangeText={(prompt) => patchQuestion({ prompt })}
        placeholder={t('jobFlow.questions.promptPlaceholder') as string}
        placeholderTextColor={theme.textTertiary}
        multiline
        style={[
          styles.promptInput,
          {
            borderColor: theme.border,
            backgroundColor: theme.background,
            color: theme.text,
          },
        ]}
      />

      <View style={styles.customControls}>
        <View style={fieldStyles.flexOne}>
          <FlowDropdown
            label={t('jobFlow.questions.answerType') as string}
            value={question.type}
            placeholder={t('jobFlow.questions.answerType') as string}
            options={typeOptions}
            onChange={(type) =>
              patchQuestion({
                type,
                options:
                  type === 'multiple_choice'
                    ? question.options ?? []
                    : undefined,
              })
            }
          />
        </View>
        <View style={styles.requiredToggle}>
          <Text style={[styles.requiredLabel, { color: theme.textSecondary }]}>
            {t('jobFlow.questions.required')}
          </Text>
          <AppSwitch
            value={question.required}
            onValueChange={(required) => patchQuestion({ required })}
            accessibilityLabel={t('jobFlow.questions.required') as string}
          />
        </View>
      </View>

      {question.type === 'multiple_choice' ? (
        <OrderedListEditor
          label={t('jobFlow.questions.options') as string}
          items={question.options ?? []}
          onChange={(options) => patchQuestion({ options })}
          placeholder={t('jobFlow.questions.optionPlaceholder') as string}
        />
      ) : null}

      <InlineError messageKey={flow.errorFor(`question:${question.id}`)} />
    </View>
  );
}

export function QuestionBuilder({ flow }: { flow: PostJobFlowController }) {
  const { t } = useTranslation('employer');
  const { theme } = useTheme();
  const haptics = useHaptics();

  const custom = flow.state.questions.custom;

  const addQuestion = () => {
    haptics.trigger('light');
    flow.setQuestions({
      custom: [
        ...custom,
        {
          id: createQuestionId(),
          prompt: '',
          type: 'short_answer',
          required: false,
          order: custom.length,
          source: 'custom',
        },
      ],
    });
  };

  return (
    <View style={styles.wrap}>
      <View style={fieldStyles.group}>
        <FieldLabel>{t('jobFlow.questions.defaultsTitle') as string}</FieldLabel>
        <Text style={[styles.hint, { color: theme.textTertiary }]}>
          {t('jobFlow.questions.defaultsHint')}
        </Text>
        <View
          style={[
            styles.defaultsCard,
            { backgroundColor: theme.surface, borderColor: theme.borderSubtle },
          ]}
        >
          {DEFAULT_QUESTION_DEFS.map((def, index) => (
            <DefaultQuestionRow key={def.key} defIndex={index} flow={flow} />
          ))}
        </View>
      </View>

      <View style={fieldStyles.group}>
        <FieldLabel>{t('jobFlow.questions.customTitle') as string}</FieldLabel>
        {custom.length === 0 ? (
          <Text style={[styles.hint, { color: theme.textTertiary }]}>
            {t('jobFlow.questions.customHint')}
          </Text>
        ) : (
          <View style={styles.customList}>
            {custom.map((question, index) => (
              <CustomQuestionCard
                key={question.id}
                question={question}
                index={index}
                total={custom.length}
                flow={flow}
              />
            ))}
          </View>
        )}

        <Pressable
          onPress={addQuestion}
          accessibilityRole="button"
          style={[styles.addButton, { backgroundColor: theme.buttonSoftBg }]}
        >
          <Plus size={16} color={colors.primary} strokeWidth={2.5} />
          <Text style={[styles.addButtonText, { color: colors.primary }]}>
            {t('jobFlow.questions.addQuestion')}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing[4],
  },
  hint: {
    fontSize: 12,
    lineHeight: 18,
  },
  defaultsCard: {
    borderRadius: borderRadius['2xl'],
    borderWidth: 1,
    paddingHorizontal: spacing[4],
  },
  defaultRow: {
    paddingVertical: spacing[3],
    gap: spacing[3],
  },
  defaultHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing[3],
  },
  defaultPrompt: {
    flex: 1,
    fontSize: 14,
    lineHeight: 19,
  },
  defaultControls: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: spacing[3],
  },
  requiredToggle: {
    alignItems: 'center',
    gap: spacing[1],
    paddingBottom: 4,
  },
  requiredLabel: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  customList: {
    gap: spacing[3],
  },
  customCard: {
    borderRadius: borderRadius['2xl'],
    borderWidth: 1,
    padding: spacing[4],
    gap: spacing[3],
  },
  customHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing[2],
  },
  customIndex: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  customActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  iconButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconDisabled: {
    opacity: 0.35,
  },
  promptInput: {
    minHeight: 48,
    borderRadius: borderRadius.xl,
    borderWidth: 1,
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[3],
    fontSize: 14,
    lineHeight: 20,
  },
  customControls: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: spacing[3],
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[2],
    minHeight: 44,
    borderRadius: 999,
    paddingHorizontal: spacing[4],
  },
  addButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
